
import type { ElementType, ReactNode, HTMLAttributes } from 'react';
import { cx } from '@client/shared/lib/cx.ts';
import styles from './Text.module.css';

type HeadingLevel = 1 | 2 | 3 | 4;
const headingSize: Record<HeadingLevel, string | undefined> = {
  1: styles.h1,
  2: styles.h2,
  3: styles.h3,
  4: styles.h4,
};

export interface HeadingProps extends HTMLAttributes<HTMLHeadingElement> {
  level?: HeadingLevel;
  as?: ElementType;
  children?: ReactNode;
}

export function Heading({ level = 2, as, className, children, ...rest }: HeadingProps) {
  const Tag = (as ?? (`h${level}` as ElementType)) as ElementType;
  return (
    <Tag className={cx(styles.heading, headingSize[level], className)} {...rest}>
      {children}
    </Tag>
  );
}

export function Eyebrow({
  as: As = 'span',
  className,
  children,
  ...rest
}: { as?: ElementType; className?: string; children?: ReactNode }) {
  return (
    <As className={cx(styles.eyebrow, className)} {...rest}>
      {children}
    </As>
  );
}

export type TextSize = 'xs' | 'sm' | 'base' | 'md' | 'lg';
export type TextTone = 'default' | 'soft' | 'muted' | 'accent' | 'danger';
export type TextWeight = 'regular' | 'medium' | 'semibold';

const sizeClass: Record<TextSize, string | undefined> = {
  xs: styles['size-xs'],
  sm: styles['size-sm'],
  base: styles['size-base'],
  md: styles['size-md'],
  lg: styles['size-lg'],
};
const toneClass: Record<TextTone, string | undefined> = {
  default: styles['tone-default'],
  soft: styles['tone-soft'],
  muted: styles['tone-muted'],
  accent: styles['tone-accent'],
  danger: styles['tone-danger'],
};
const weightClass: Record<TextWeight, string | undefined> = {
  regular: styles['weight-regular'],
  medium: styles['weight-medium'],
  semibold: styles['weight-semibold'],
};

export interface TextProps extends HTMLAttributes<HTMLElement> {
  size?: TextSize;
  tone?: TextTone;
  weight?: TextWeight;
  as?: ElementType;
  children?: ReactNode;
}

export function Text({
  size = 'base',
  tone = 'default',
  weight = 'regular',
  as: As = 'p',
  className,
  children,
  ...rest
}: TextProps) {
  return (
    <As
      className={cx(styles.text, sizeClass[size], toneClass[tone], weightClass[weight], className)}
      {...rest}
    >
      {children}
    </As>
  );
}
